import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FamilyBar, NotificationEntry, Sidebar, useConfirm, type NotificationEntryHandle } from '@beyond-js/ui/react';
import wordmark from '@brand/beyond-logo-wordmark.svg';
import { config } from '@snapshots/config';
import { Names, type Descriptor, type Place } from '@snapshots/domain';
import { usePresence, useRenewal } from '@snapshots/session';
import { device } from '@snapshots/theme';
import { Address } from './address';
import { Arrival } from './arrival';
import { Copies } from './copy';
import { Everywhere } from './everywhere';
import type { Frame } from './frame';
import { Leaving } from './leaving';
import { Returned } from './returned';

/** The overview of one organization's captures, as an address of this interface */
function scope(organization: string): string {
	return new URL(`/?${new URLSearchParams({ organization })}`, window.location.origin).href;
}

/**
 * The frame every signed-in screen sits in: the family bar (decision D01), identical in every
 * product, which names the person, the organization and project in view and switches product while
 * keeping the project (D04); this product's own sections in the family's `Sidebar` (D53), permanent
 * from 1024 px and a product row with a drawer below, naming the project or organization in view as
 * text; the notification entry and the account menu, whose product group opens the family's one
 * "Language and appearance" dialog (D54). Each screen draws its own `Page` in the main element.
 *
 * The place in view is the overview's `?project=` or `?organization=`, or the project of the capture
 * being read, which the capture page reports through the outlet's `Frame`. The descriptor comes from this service's
 * relay to Beyond Projects; while it loads, or when it cannot be read, the bar shows the names this
 * application knows itself and never blocks the page.
 *
 * A link that stays in this interface is followed inside the application; one that leads to another
 * product leaves for it. Nothing the bar lists is a permission: the destination checks the person.
 *
 * Since `@beyond-js/ui` 0.4.0 the organization menu changes the organization in view here (D03): each
 * row, from the descriptor or, while it loads or Projects cannot answer, from the Accounts standing, is
 * this overview for that organization. The project menu is the family's chooser; Snapshots binds no
 * mappings, so its rows carry no state, and it has no projects page, so "All projects" is the
 * organization in Projects. Accounts' pages in the profile menu come back here with `returned=accounts`,
 * which `Returned` takes out of the address.
 */
export function Shell() {
	const { text, language, notices, family, identity, end, after, unsaved, client, session, adopt } = usePresence();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [parameters] = useSearchParams();
	const [held, setHeld] = useState<Place>();
	const [descriptor, setDescriptor] = useState<Descriptor | { unavailable: true } | null>(null);
	// The full inbox and the entry are separate views of one read state: a change in either recounts the entry
	const entry = useRef<NotificationEntryHandle>(null);
	useEffect(() => notices.listen(() => entry.current?.refresh()), [notices]);

	// A capture's place is known once it is read: until then the bar is not asked, so it never shows a guess
	const pending = (pathname.startsWith('/snapshots/') || pathname.startsWith('/captures/')) && held === undefined;
	const place: Place = pathname === '/' ? { project: parameters.get('project') || undefined, organization: parameters.get('project') ? undefined : parameters.get('organization') || undefined } : (held ?? {});
	// Names the session and every descriptor gave, by identifier: while a later one cannot be read the bar still says them
	const names = useMemo(() => new Names(), []);
	names.learn(identity);
	// A move to another screen, or to another project or organization's list, takes focus to its heading
	const arrival = useMemo(() => new Arrival(), []);
	arrival.at(`${pathname}|${place.project ?? ''}|${place.organization ?? ''}`);
	useEffect(() => {
		if (pending) return;
		let live = true;
		void family.describe({ project: place.project, organization: place.organization }).then(found => {
			if (!live) return;
			names.learn(found);
			setDescriptor(found);
		});
		return () => {
			live = false;
		};
	}, [family, names, pending, place.project, place.organization]);

	// Signing out goes on to Accounts' `/leave` when the descriptor names it (Q09): the menu then says so
	const beyond = Boolean(descriptor && !('unavailable' in descriptor) && descriptor.links?.leave);
	const labels = useMemo(() => ({ family: Copies.family(language, text, beyond), notifications: Copies.notifications(language) }), [language, text, beyond]);
	const sections = Copies.sidebar(language);
	const questions = useConfirm(Copies.questions(language));
	// "Sign out of Beyond" in the bar's own form: a title typed and not saved is asked about first, then
	// this session ends within the bar's bound and the bar goes to `/leave`, or `after` signs out here only
	const signout = {
		before: async () => {
			const name = unsaved.first;
			if (name === undefined) return true;
			return questions.confirm({
				title: text('unsaved.title'),
				message: text('unsaved.message', { title: name }),
				consequence: { lost: text('unsaved.lost'), kept: text('unsaved.kept') },
				accept: text(beyond ? 'unsaved.accept' : 'family.signout'),
				cancel: text('unsaved.keep'),
				tone: 'danger'
			});
		},
		end: () => end(beyond),
		after
	};
	const leave = descriptor && !('unavailable' in descriptor) ? descriptor.links?.leave : undefined;
	// The session that ends while the person works (the family rule): renewed without a page, or one
	// dialog that cannot be dismissed, whose "Use another account" is this bar's "Sign out of Beyond".
	// Created once the descriptor settled, which names Beyond Accounts' account page for a suspended account
	const { renewal, renewed, resumed } = useRenewal({
		client,
		session,
		identity,
		ready: descriptor !== null,
		accounts: (descriptor && !('unavailable' in descriptor) ? (descriptor.links?.manage?.account ?? descriptor.links?.account) : undefined) ?? null,
		language,
		other: () => void Leaving.run({ signout, address: leave }),
		adopt
	});
	// This product's own home: the switcher's one entry without a descriptor. The wordmark leads to Projects through `links.home`
	const brand = useMemo(() => ({ src: wordmark, href: '/' }), []);
	const person = identity?.name ?? identity?.account;
	const organization = identity?.organizations.find(entry => entry.organization === place.organization);
	const known = (id: string | undefined) => (id ? (names.name(id) ?? id) : undefined);
	// The addresses this application knows itself, for a bar without a descriptor: Projects, when configured
	const links = config.projects ? { home: config.projects } : undefined;
	const organizations = (identity?.organizations ?? []).map(entry => ({ id: entry.organization, name: entry.name ?? entry.organization, role: entry.role, url: scope(entry.organization), current: entry.organization === place.organization }));
	const fallback = { person, organization: organization?.name ?? known(place.organization), project: place.project ? (names.name(place.project) ?? text('nav.project.unknown')) : undefined, organizations, links };
	// Each organization of the descriptor is this overview for it, as in the fallback
	const relayed = useMemo(() => (descriptor && !('unavailable' in descriptor) ? { ...descriptor, organizations: descriptor.organizations?.map(entry => ({ ...entry, url: scope(entry.id) })) } : descriptor), [descriptor]);

	const inside = (destination: string): string | undefined => {
		const url = new URL(destination, window.location.href);
		return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : undefined;
	};
	const follow = (destination: string) => {
		const path = inside(destination);
		if (path) navigate(path);
		else window.location.assign(destination);
	};
	const route = (href: string | null | undefined, event: MouseEvent | ReactMouseEvent) => {
		const path = href ? inside(href) : undefined;
		if (!path || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
		event.preventDefault();
		navigate(path);
	};
	const frame: Frame = useMemo(() => ({ place: setHeld, descriptor, names, arrival, renewal, renewed, resumed }), [descriptor, names, arrival, renewal, renewed, resumed]);
	// A breadcrumb of a page's header is a link of this interface: followed here, like the bar's
	const crumbs = (event: ReactMouseEvent) => {
		const link = (event.target as Element).closest?.('.bui-crumbs a');
		if (link) route(link.getAttribute('href'), event);
	};

	// "Change for all of Beyond" comes back to the screen and scope in view (`Everywhere`)
	const account = descriptor && !('unavailable' in descriptor) ? descriptor.links?.account : undefined;
	const back = pathname === '/' ? Address.of(place) : pathname;
	const preferences = useMemo(() => ({ preferences: device.preferences, everywhere: Everywhere.address(account, back) }), [account, back]);
	// Storage is an organization's: the one in view when there is one
	const shown = place.organization ?? (descriptor && !('unavailable' in descriptor) ? descriptor.organization?.id : undefined);
	const storage = shown ? `/storage?${new URLSearchParams({ organization: shown })}` : '/storage';
	// What the sections belong to, as text: the project in view, or else the organization. A snapshot and a
	// comparison belong to a project known once read: the line holds its place meanwhile, naming none (D185)
	const holding = held === undefined && (pending || pathname === '/compare');
	const context = holding ? { label: text('nav.project'), name: '\u00a0' } : place.project ? { label: text('nav.project'), name: names.name(place.project) ?? text('nav.project.unknown') } : place.organization ? { label: text('nav.organization'), name: known(place.organization) ?? place.organization } : null;

	return (
		<div className="min-h-screen bg-canvas text-ink">
			<FamilyBar
				product="snapshots"
				brand={brand}
				descriptor={relayed}
				fallback={fallback}
				labels={labels.family}
				onNavigate={(item, event) => route(item.url, event)}
				notifications={<NotificationEntry ref={entry} adapter={notices} href="/notifications" onView={event => route('/notifications', event)} onOpen={destination => follow(destination)} products={Copies.products} locale={language} interval={60000} labels={labels.notifications} />}
				account={{ signout, preferences }}
			/>
			<Returned />
			<div className="bui-shell">
				<Sidebar
					product="Snapshots"
					navigation={{ key: 'snapshots' }}
					labels={sections}
					context={context}
					groups={[
						{
							items: [
								{ label: text('nav.captures'), glyph: 'camera', href: '/', current: pathname === '/' || pathname.startsWith('/snapshots/') || pathname.startsWith('/captures/') || pathname === '/compare' },
								{ label: text('nav.storage'), glyph: 'archive', href: storage, current: pathname === '/storage' },
								{ label: text('nav.notifications'), glyph: 'bell', href: '/notifications', current: pathname === '/notifications' },
								// A Beyond operator's own entry; the service still decides every read and change
								identity?.operator ? { label: text('nav.operator'), glyph: 'key', href: '/operator', current: pathname === '/operator' } : null
							]
						}
					]}
					onNavigate={(item, event) => route(item.href, event)}
				/>
				<main onClick={crumbs}>
					<Outlet context={frame} />
				</main>
			</div>
		</div>
	);
}
